import { baseApi } from '@/lib/utils'

export type VendorType = 'freelance' | 'company'
export type VendorPaymentType = 'full' | 'milestone'

export interface VendorSyncedProject {
  list_id: number
  list_name: string
}

export interface Vendor {
  vendor_id: number
  vendor_name: string
  vendor_type: VendorType
  is_active: boolean
  setup_completed: boolean
  default_rate?: number
  clickup_folder_id?: number
  // display-only reference of this vendor's ClickUp projects, synced from
  // the Lists inside their vendor folder - never linked into real projects
  synced_projects?: VendorSyncedProject[]
  // contact
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  // type-conditional
  freelancer_name?: string
  company_name?: string
  address?: string
  tech_stack?: string[]
}

export interface VendorsResponse {
  data: Vendor[]
  pagination: {
    totalRecords: number
    totalPages: number
    currentPage: number
    limit: number
  }
}

export interface GetVendorsParams {
  page?: number
  limit?: number
  search?: string
  is_active?: boolean
  setup_completed?: boolean
}

export interface VendorOption {
  vendor_id: number
  vendor_name: string
  default_rate?: number
}

export interface CreateVendorPayload {
  vendor_name: string
  vendor_type: VendorType
  contact_name: string
  contact_email: string
  contact_phone: string
  default_rate?: number
  freelancer_name?: string
  company_name?: string
  address?: string
  tech_stack?: string[]
  folder_id?: number
}

export interface UpdateVendorPayload extends Partial<CreateVendorPayload> {
  id: number
  is_active?: boolean
  setup_completed?: boolean
}

export interface VendorHour {
  id: number
  project_id: number
  vendor_id: number
  vendor_name?: string
  date: string
  hours: number
  description?: string
  rate?: number
  total_cost?: number
  created_at?: string
}

export interface VendorHoursResponse {
  data: VendorHour[]
  pagination: {
    totalRecords: number
    totalPages: number
    currentPage: number
    limit: number
  }
}

export interface GetVendorHoursParams {
  page?: number
  limit?: number
  start_date?: string
  end_date?: string
  sort_by?: string
  order?: 'ASC' | 'DESC'
}

export interface CreateVendorHourPayload {
  vendor_id: number
  date: string
  hours: number
  description?: string
  rate?: number
}

export interface UpdateVendorHourPayload extends Partial<CreateVendorHourPayload> {
  id: number
}

/* ── Vendor payment schedule (fixed/milestone payments) ──
   Mutually exclusive with vendor hours: a project's vendor cost comes from
   exactly one of the two, driven by project_vendors.vendor_payment_type. */
export type VendorPaymentStatus = 'unset' | 'pending' | 'partial' | 'paid'

export interface VendorPayment {
  id: number
  project_id: number
  vendor_id: number
  project_milestone_id?: number | null
  clickup_task_id?: string | null
  milestone_name: string
  start_date?: string | null
  end_date?: string | null
  is_completed?: boolean | null
  created_at?: string
  updated_at?: string
  // finance-only - masked by the backend for everyone else
  amount?: number | null
  released?: number
  balance?: number | null
  status?: VendorPaymentStatus
}

export interface VendorPaymentsResponse {
  data: VendorPayment[]
}

export interface UpdateVendorPaymentAmountPayload {
  projectId: number
  id: number
  amount: number | null
}

export interface CreateVendorPaymentReleasePayload {
  projectId: number
  id: number
  amount_released: number
  description: string
}

export interface UpdateVendorPaymentNamePayload {
  projectId: number
  id: number
  milestone_name: string
}

export interface DeleteVendorPaymentSchedulePayload {
  projectId: number
  id: number
}

export const vendorApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getVendors: builder.query<VendorsResponse, GetVendorsParams | void>({
      query: params => ({
        url: '/vendors',
        params: params ?? {},
      }),
      providesTags: ['Vendors'],
    }),

    getVendorById: builder.query<{ data: Vendor }, number>({
      query: id => `/vendors/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Vendors' as const, id }],
    }),

    getVendorSetupList: builder.query<
      VendorsResponse,
      { page?: number; limit?: number; search?: string } | void
    >({
      query: params => ({
        url: '/vendors/setup',
        params: params ?? {},
      }),
      providesTags: ['Vendors'],
    }),

    getVendorOptions: builder.query<{ data: VendorOption[] }, void>({
      query: () => '/vendors/select-options',
      providesTags: ['Vendors'],
    }),

    createVendor: builder.mutation<{ data: Vendor }, CreateVendorPayload>({
      query: data => ({ url: '/vendors', method: 'POST', body: data }),
      invalidatesTags: ['Vendors'],
    }),

    updateVendor: builder.mutation<{ data: Vendor }, UpdateVendorPayload>({
      query: ({ id, ...data }) => ({
        url: `/vendors/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Vendors' as const, id },
        'Vendors',
      ],
    }),

    getVendorHours: builder.query<
      VendorHoursResponse,
      { projectId: number } & GetVendorHoursParams
    >({
      query: ({ projectId, ...params }) => ({
        url: `/projects/${projectId}/vendor-hours`,
        params,
      }),
      providesTags: (_r, _e, { projectId }) => [
        { type: 'VendorHours' as const, id: projectId },
      ],
    }),

    addVendorHour: builder.mutation<
      { data: VendorHour },
      { projectId: number; data: CreateVendorHourPayload }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/vendor-hours`,
        method: 'POST',
        body: data,
      }),
      // Vendor hours cost feeds project total cost/profit (see
      // mergeVendorCost server-side), so the project's cached details/list
      // figures need to refetch too, not just this project's hours list.
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'VendorHours' as const, id: projectId },
        { type: 'ProjectDetails' as const, id: projectId },
        'Projects',
      ],
    }),

    updateVendorHour: builder.mutation<
      { data: VendorHour },
      { projectId: number; data: UpdateVendorHourPayload }
    >({
      query: ({ projectId, data: { id, ...rest } }) => ({
        url: `/projects/${projectId}/vendor-hours/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'VendorHours' as const, id: projectId },
        { type: 'ProjectDetails' as const, id: projectId },
        'Projects',
      ],
    }),

    deleteVendorHour: builder.mutation<void, { projectId: number; id: number }>(
      {
        query: ({ projectId, id }) => ({
          url: `/projects/${projectId}/vendor-hours/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, { projectId }) => [
          { type: 'VendorHours' as const, id: projectId },
          { type: 'ProjectDetails' as const, id: projectId },
          'Projects',
        ],
      }
    ),

    getVendorPayments: builder.query<
      VendorPaymentsResponse,
      { projectId: number }
    >({
      query: ({ projectId }) => `/projects/${projectId}/vendor-payments`,
      providesTags: (_r, _e, { projectId }) => [
        { type: 'VendorPayments' as const, id: projectId },
      ],
    }),

    updateVendorPaymentAmount: builder.mutation<
      { message: string },
      UpdateVendorPaymentAmountPayload
    >({
      query: ({ projectId, id, amount }) => ({
        url: `/projects/${projectId}/vendor-payments/${id}`,
        method: 'PATCH',
        body: { amount },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'VendorPayments' as const, id: projectId },
        { type: 'ProjectDetails' as const, id: projectId },
        'Projects',
      ],
    }),

    updateVendorPaymentName: builder.mutation<
      { message: string },
      UpdateVendorPaymentNamePayload
    >({
      query: ({ projectId, id, milestone_name }) => ({
        url: `/projects/${projectId}/vendor-payments/${id}/name`,
        method: 'PATCH',
        body: { milestone_name },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'VendorPayments' as const, id: projectId },
      ],
    }),

    deleteVendorPaymentSchedule: builder.mutation<
      { message: string },
      DeleteVendorPaymentSchedulePayload
    >({
      query: ({ projectId, id }) => ({
        url: `/projects/${projectId}/vendor-payments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'VendorPayments' as const, id: projectId },
        { type: 'ProjectDetails' as const, id: projectId },
        'Projects',
      ],
    }),

    createVendorPaymentRelease: builder.mutation<
      { data: { id: number } },
      CreateVendorPaymentReleasePayload
    >({
      query: ({ projectId, id, ...body }) => ({
        url: `/projects/${projectId}/vendor-payments/${id}/releases`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'VendorPayments' as const, id: projectId },
        { type: 'ProjectDetails' as const, id: projectId },
        'Projects',
      ],
    }),

    syncVendorPaymentMilestones: builder.mutation<
      { message: string },
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/vendor-payments/sync-clickup-milestones`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'VendorPayments' as const, id: projectId },
        { type: 'ProjectDetails' as const, id: projectId },
        'Projects',
      ],
    }),
  }),
})

export const {
  useGetVendorsQuery,
  useLazyGetVendorsQuery,
  useGetVendorByIdQuery,
  useGetVendorSetupListQuery,
  useGetVendorOptionsQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useGetVendorHoursQuery,
  useAddVendorHourMutation,
  useUpdateVendorHourMutation,
  useDeleteVendorHourMutation,
  useGetVendorPaymentsQuery,
  useLazyGetVendorPaymentsQuery,
  useUpdateVendorPaymentAmountMutation,
  useUpdateVendorPaymentNameMutation,
  useDeleteVendorPaymentScheduleMutation,
  useCreateVendorPaymentReleaseMutation,
  useSyncVendorPaymentMilestonesMutation,
} = vendorApi
